import { useContext, useState } from "react";
import {
  Tooltip,
  IconButton,
  MenuItem,
  Button,
  TextField,
  InputAdornment,
  Typography,
  Divider,
  Modal,
  Box,
} from "@mui/material";
import GridOnIcon from "@mui/icons-material/GridOn";
import { useTranslation } from "react-i18next";
import { regenerateGrid } from "../../../../api/ApiGrid";
import AppContext from "../../../Contexts/AppContext";
import ClearIcon from "@mui/icons-material/Clear";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";

const ChangeGridTool = ({ layer }) => {
  const { t, i18n } = useTranslation("main");
  const { layersGroupsRef, project, setProject } = useContext(AppContext);
  const [open, setOpen] = useState(false);
  const [selectedLayer, setSelectedLayer] = useState();
  const [showMore, setShowMore] = useState(false);

  const initialNames = {
    en: "Work grid - Teledetection",
    es: "Rejilla de trabajo - Teledetección",
  };
  const [nameEn, setNameEn] = useState(initialNames.en);
  const [nameEs, setNameEs] = useState(initialNames.es);

  const sizeOptions = [
    { label: t("small"), val: 250 },
    { label: t("medium"), val: 500 },
    { label: t("large"), val: 1000 },
  ];
  const [selectedSize, setSelectedSize] = useState(sizeOptions[1]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setNameEn(initialNames.en);
    setNameEs(initialNames.es);
    setSelectedLayer(null);
    setShowMore(false);
    setSelectedSize(sizeOptions[1]);
    setOpen(false);
  };

  const handleSave = async () => {
    const toast_id_updating_element = toast.loading(
      `${t("regenerating_grid")}...`,
      {
        position: "top-right",
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      }
    );
    await regenerateGrid({
      project_uuid: project?.uuid,
      grid_uuid: layer.uuid,
      layer_uuid: selectedLayer?.uuid,
      size: selectedSize?.val,
      layer_name_en: nameEn,
      layer_name_es: nameEs,
    });
    toast.update(toast_id_updating_element, {
      render: t("grid_regenerated_successfully"),
      type: "success",
      isLoading: false,
      autoClose: 5000,
    });
    setProject((prev) => ({ ...prev }));
    handleClose();
  };

  return (
    <>
      <Tooltip title={t("tooltipChangeGrid")}>
        <IconButton onClick={handleOpen}>
          <GridOnIcon />
        </IconButton>
      </Tooltip>

      <Modal open={open} onClose={handleClose} aria-labelledby="modal-title">
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            maxWidth: "90vw",
          }}
        >
          <IconButton
            style={{
              position: "absolute",
              top: "1rem",
              right: "1rem",
            }}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" id="modal-title" sx={{ mb: 1 }}>
            {t("tooltipChangeGrid")}
          </Typography>

          <TextField
            value={selectedLayer ?? ""}
            onChange={(e) => setSelectedLayer(e.target.value)}
            label={t("from_layer")}
            fullWidth
            select
            InputProps={{
              endAdornment: selectedLayer && (
                <InputAdornment position="end" style={{ marginRight: "20px" }}>
                  <IconButton onClick={() => setSelectedLayer("")} size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            variant="outlined"
          >
            {layersGroupsRef.current
              .find((lg) => lg.id === layer.layer_group_id)
              .layers?.filter((lay) => lay !== layer)
              .map((option) => (
                <MenuItem key={option} value={option}>
                  {option.name[i18n.language]}
                </MenuItem>
              ))}
          </TextField>

          <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
            <Typography
              style={{ cursor: "pointer" }}
              onClick={() => setShowMore((prev) => !prev)}
            >
              {showMore ? `- ${t("less")}` : `+ ${t("more")}`}
            </Typography>
            <Divider style={{ flexGrow: 1 }} />
          </div>

          {/* {showMore && ( */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              // overflow: showMore ? "visible" : "hidden",
              overflow: "hidden",
              transition: "all 0.5s ease-in-out",
              maxHeight: showMore ? "220px" : "0px",
              // paddingTop: showMore ? "1rem" : "",
            }}
          >
            <TextField
              style={{ marginTop: "8px" }}
              value={selectedSize ? selectedSize.val : ""}
              onChange={(e) => {
                const newValue = parseInt(e.target.value, 10);
                const selectedOption = sizeOptions.find(
                  (option) => option.val === newValue
                );
                setSelectedSize(selectedOption);
              }}
              label={t("size")}
              fullWidth
              select
              InputProps={{
                endAdornment: selectedSize && (
                  <InputAdornment
                    position="end"
                    style={{ marginRight: "20px" }}
                  >
                    <IconButton
                      onClick={() => setSelectedSize(null)}
                      size="small"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              variant="outlined"
            >
              {sizeOptions.map((option) => (
                <MenuItem key={option.val} value={option.val}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              value={nameEn}
              onChange={(e) => setNameEn(e.target.value)}
              label={t("name_en")}
              fullWidth
              InputProps={{
                endAdornment: nameEn && (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setNameEn("")} size="small">
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              variant="outlined"
            />

            <TextField
              value={nameEs}
              onChange={(e) => setNameEs(e.target.value)}
              label={t("name_es")}
              fullWidth
              InputProps={{
                endAdornment: nameEs && (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setNameEs("")} size="small">
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              variant="outlined"
            />
          </Box>
          {/* )} */}

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Tooltip title={!selectedLayer ? t("select_layer") : ""}>
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  disabled={!selectedLayer}
                >
                  {t("save")}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export { ChangeGridTool };
